import axios, { AxiosError, AxiosInstance } from "axios";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import { notifyDataMutation } from "@/utilities/mutation-bus";

// Mensaje genérico para 403. Se evita acoplarlo a i18n acá (el interceptor
// vive afuera del React tree y no tiene contexto de idioma). Si más adelante
// se necesita traducir, exponer un setter desde un provider.
const FORBIDDEN_MESSAGE = "No tienes permisos para realizar esta acción";

export const axiosInstance: AxiosInstance = axios.create({
  baseURL: "",
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
});


// @ts-ignore
if (!axiosInstance.interceptorsRegistered) {
  // @ts-ignore
  axiosInstance.interceptorsRegistered = true;

  axiosInstance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );


  axiosInstance.interceptors.response.use(
    (response) => {
      // Toda escritura que salió OK avisa por el bus, para que las vistas que muestran datos
      // derivados puedan refrescarse sin recargar la página (hoy: el Historial).
      //
      // Acá y no en cada call site porque el dataProvider de Refine usa este mismo
      // `axiosInstance`: con este único punto quedan cubiertas las llamadas directas Y todo el
      // CRUD de Refine, incluidas las acciones que se agreguen en el futuro.
      //
      // Se excluye `/history`: son las escrituras del propio Historial (notas, pin, programar),
      // que ya recargan por su cuenta. Sin esta guarda cada nota dispararía una recarga doble.
      const method = (response.config.method ?? "").toLowerCase();
      const url = response.config.url ?? "";
      if (["post", "put", "patch", "delete"].includes(method) && !url.includes("/history")) {
        notifyDataMutation({ url, method });
      }
      return response;
    },
    async (error: AxiosError) => {
      if (!error.response) {
       
        console.error("Network error:", error);
        return Promise.reject(error);
      }

      const status = error.response.status;

      // Si el token expiró → forzamos logout y redirección
      if (status === 401) {
        console.warn("Unauthorized → limpiando sesión");

        localStorage.removeItem("token");
        localStorage.removeItem("user");
        // Si querés limpiar otros caches de refine o react-query, hacelo acá
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }

        return Promise.reject(error);
      }

      // 403 → permission denied. Reemplazamos:
      //  - `error.response.data.message` (catches que leen body del backend).
      //  - `error.message` (catches que leen el message default de axios —
      //    p.ej. "Request failed with status code 403").
      // NO mostramos toast desde el interceptor: los componentes ya hacen
      // `message.error(e.message)` o equivalente, y con el override de
      // `error.message` ven el texto amigable. Mostrar toast acá generaba
      // duplicados.
      if (status === 403) {
        if (
          error.response.data &&
          typeof error.response.data === "object"
        ) {
          (error.response.data as any).message = FORBIDDEN_MESSAGE;
        }
        try {
          error.message = FORBIDDEN_MESSAGE;
        } catch {
          try {
            Object.defineProperty(error, "message", {
              value: FORBIDDEN_MESSAGE,
              writable: true,
              configurable: true,
            });
          } catch {
            // si no se puede sobrescribir, queda el default de axios.
          }
        }
        return Promise.reject(error);
      }

      return Promise.reject(error);
    },
  );
}
